import { z } from 'zod';
import { DOCGEN_ERROR_CODES } from '../constants/error-codes';

export const documentGenerationErrorCodeSchema = z.enum(DOCGEN_ERROR_CODES, {
  error: 'Code d erreur de génération invalide.',
});
export const documentGenerationKindSchema = z.enum(['docx', 'pdf', 'pptx', 'xlsx'], {
  error: 'Format de génération non supporté.',
});

export const documentProfileSchema = z.enum([
  'company_profile',
  'custom',
  'executive_report',
  'meeting_minutes',
  'memo',
  'presentation',
  'proposal',
  'report',
  'spreadsheet',
], {
  error: 'Profil documentaire non supporté.',
});

export const documentGenerationSourceBriefUrlSchema = z.object({
  citationKey: z.string().trim().min(1).optional(),
  domain: z.string().trim().min(1).optional(),
  excerpt: z.string().trim().min(1).max(4_000).optional(),
  fetched: z.boolean().optional(),
  title: z.string().trim().min(1).optional(),
  url: z.string().trim().min(1, {
    error: 'L URL de la source est requise.',
  }),
}).strict();

export const documentGenerationSourceBriefRagCitationSchema = z.object({
  documentTitle: z.string().trim().min(1).optional(),
  excerpt: z.string().trim().min(1).max(4_000),
  page: z.number().int().positive().optional(),
  referenceId: z.string().trim().min(1).optional(),
}).strict();

export const documentGenerationSourceBriefSchema = z.object({
  collectedAt: z.string().datetime({
    error: 'La date de collecte du brief sources est invalide.',
  }),
  fictionalDataMode: z.enum(['allowed', 'forbidden', 'unspecified']).default('unspecified'),
  ragCitations: z.array(documentGenerationSourceBriefRagCitationSchema).max(24).default([]),
  urls: z.array(documentGenerationSourceBriefUrlSchema).max(24).default([]),
}).strict();

export const documentContentPlanSectionSchema = z.object({
  bullets: z.array(z.string().trim().min(1)).max(12).optional(),
  chartCaption: z.string().trim().min(1).max(200).optional(),
  chartKind: z.enum(['bar', 'donut', 'grouped_bar', 'line', 'multi_line']).optional(),
  items: z.array(z.object({
    label: z.string().trim().min(1).max(40),
    value: z.string().trim().min(1).max(40),
  }).strict()).max(4).optional(),
  kind: z.enum([
    'body',
    'bullet',
    'callout',
    'chart',
    'cover',
    'heading',
    'kpi_band',
    'numbered',
    'table',
    'toc',
  ]),
  level: z.number().int().min(1).max(3).optional(),
  rows: z.array(z.array(z.string().trim().min(1).max(120)).max(8)).max(20).optional(),
  text: z.string().trim().min(1).max(2_000).optional(),
  title: z.string().trim().min(1).max(160).optional(),
}).strict();

export const documentContentPlanSchema = z.object({
  disclaimerRequired: z.boolean().default(false),
  entity: z.string().trim().min(1).max(120).optional(),
  sections: z.array(documentContentPlanSectionSchema).min(1).max(40),
  sourcesSectionRequired: z.boolean().default(false),
  subtitle: z.string().trim().min(1).max(200).optional(),
  title: z.string().trim().min(1).max(160),
}).strict();

export const documentGenerationSpecSchema = z.object({
  data: z.json().optional(),
  instructions: z.string().trim().min(1, {
    error: 'Les instructions de génération sont requises.',
  }).max(50_000, {
    error: 'Les instructions de génération sont trop longues.',
  }),
  title: z.string().trim().min(1, {
    error: 'Le titre du document est requis.',
  }).max(160, {
    error: 'Le titre du document est trop long.',
  }),
}).strict();

export const documentGenerationRequestSchema = z.object({
  conversationId: z.uuid({
    error: 'L identifiant de conversation est invalide.',
  }),
  kind: documentGenerationKindSchema,
  spec: documentGenerationSpecSchema,
}).strict();

export const documentGenerationToolInputSchema = z.object({
  instructions: z.string().trim().min(1, {
    error: 'Les instructions de génération sont requises.',
  }).max(50_000, {
    error: 'Les instructions de génération sont trop longues.',
  }),
  kind: documentGenerationKindSchema,
  title: z.string().trim().min(1, {
    error: 'Le titre du document est requis.',
  }).max(160, {
    error: 'Le titre du document est trop long.',
  }),
}).strict();

export const documentGenerationToolOutputSchema = z.discriminatedUnion('status', [
  z.object({
    conversationId: z.uuid(),
    generationId: z.uuid(),
    generatedDocumentId: z.uuid().optional(),
    kind: documentGenerationKindSchema,
    revisionNumber: z.number().int().positive().optional(),
    status: z.literal('pending'),
    title: z.string().trim().min(1).max(160),
  }).strict(),
  z.object({
    errorCode: documentGenerationErrorCodeSchema,
    kind: documentGenerationKindSchema,
    status: z.literal('error'),
    title: z.string().trim().min(1).max(160),
  }).strict(),
]);

export const documentFactualityModeSchema = z.enum(['fictional_allowed', 'strict'], {
  error: 'Mode de factualité non supporté.',
});

export const documentGenerationOperationSchema = z.enum(['create', 'revise'], {
  error: 'Opération de génération documentaire non supportée.',
});

export const documentConstraintSchema = z.object({
  description: z.string().trim().min(1).max(500),
  key: z.string().trim().min(1).max(100),
  source: z.enum(['profile_default', 'system', 'user']),
}).strict();

export const documentBriefSchema = z.object({
  audience: z.string().trim().min(1).max(500).nullable(),
  contentRequirements: z.array(documentConstraintSchema).max(50),
  depth: z.enum(['balanced', 'detailed', 'short']),
  factualityMode: documentFactualityModeSchema,
  forbiddenElements: z.array(documentConstraintSchema).max(50),
  format: documentGenerationKindSchema,
  profile: documentProfileSchema,
  purpose: z.string().trim().min(1).max(2_000),
  sourceRequirements: z.array(documentConstraintSchema).max(50),
  styleRequirements: z.array(documentConstraintSchema).max(50),
  title: z.string().trim().min(1).max(160),
  tone: z.string().trim().min(1).max(200).nullable(),
  version: z.literal(1),
}).strict();

export const documentPlannedSectionSchema = z.object({
  key: z.string().trim().min(1).max(100),
  purpose: z.string().trim().min(1).max(500),
  requirements: z.array(z.string().trim().min(1).max(500)).max(20),
  sourceKeys: z.array(z.string().trim().min(1).max(100)).max(20),
  title: z.string().trim().min(1).max(160),
}).strict();

export const documentSourceUsageSchema = z.object({
  referenceId: z.string().trim().min(1).max(200),
  sectionKeys: z.array(z.string().trim().min(1).max(100)).max(40),
  sourceType: z.enum(['rag', 'url', 'user_input']),
}).strict();

export const documentCanonicalContentPlanSchema = z.object({
  sections: z.array(documentPlannedSectionSchema).min(1).max(40),
  sourceUsage: z.array(documentSourceUsageSchema).max(50),
  version: z.literal(1),
}).strict();

const documentHexColorSchema = z.string().regex(/^[0-9A-F]{6}$/i, {
  error: 'La couleur doit être un code hexadécimal à six caractères sans #.',
});

export const documentVisualComponentSchema = z.object({
  justification: z.string().trim().min(1).max(500),
  key: z.string().trim().min(1).max(100),
  kind: z.enum(['callout', 'chart', 'image', 'kpi_band', 'table', 'toc']),
  sectionKey: z.string().trim().min(1).max(100).nullable(),
}).strict();

export const documentPaletteSchema = z.object({
  accent: documentHexColorSchema,
  background: documentHexColorSchema,
  primary: documentHexColorSchema,
  secondary: documentHexColorSchema,
  text: documentHexColorSchema,
}).strict();

export const documentTypographySchema = z.object({
  bodyFont: z.string().trim().min(1).max(100),
  headingFont: z.string().trim().min(1).max(100),
}).strict();

export const documentVisualPlanSchema = z.object({
  components: z.array(documentVisualComponentSchema).max(50),
  cover: z.object({
    enabled: z.boolean(),
    layout: z.enum(['minimal', 'none', 'split', 'standard']),
  }).strict(),
  palette: documentPaletteSchema,
  typography: documentTypographySchema,
  version: z.literal(1),
}).strict().refine(
  (plan) => plan.cover.enabled ? plan.cover.layout !== 'none' : plan.cover.layout === 'none',
  {
    error: 'Le layout de couverture doit être none lorsque la couverture est désactivée.',
    path: ['cover', 'layout'],
  },
);

export const documentRevisionPatchSchema = z.object({
  addContentRequirements: z.array(documentConstraintSchema).max(20).default([]),
  addForbiddenElements: z.array(documentConstraintSchema).max(20).default([]),
  addStyleRequirements: z.array(documentConstraintSchema).max(20).default([]),
  removeConstraintKeys: z.array(z.string().trim().min(1).max(100)).max(20).default([]),
  requestedContentChanges: z.array(z.string().trim().min(1).max(1_000)).max(20).default([]),
  requestedVisualChanges: z.array(z.string().trim().min(1).max(1_000)).max(20).default([]),
}).strict();

export const documentGenerationStatusSchema = z.enum(
  ['dead', 'failed', 'generating', 'pending', 'succeeded'],
  { error: 'Statut de génération invalide.' },
);

export const documentGenerationAcceptedSchema = z.object({
  generatedDocumentId: z.uuid().optional(),
  generationId: z.uuid({
    error: 'L identifiant de génération est invalide.',
  }),
  revisionNumber: z.number().int().positive().optional(),
  status: z.literal('pending'),
}).strict();

export const documentGenerationResultSchema = z.object({
  contentType: z.string().trim().min(1, {
    error: 'Le type de contenu du document généré est requis.',
  }),
  downloadUrl: z.url({
    error: 'L URL de téléchargement du document généré est invalide.',
  }),
  expiresIn: z.number().int().positive({
    error: 'La durée de validité du téléchargement est invalide.',
  }),
  filename: z.string().trim().min(1, {
    error: 'Le nom du document généré est requis.',
  }),
  generationId: z.uuid({
    error: 'L identifiant de génération est invalide.',
  }),
  generatedDocumentId: z.uuid().optional(),
  kind: documentGenerationKindSchema,
  objectKey: z.string().trim().min(1, {
    error: 'La clef de stockage du document généré est requise.',
  }),
  revisionNumber: z.number().int().positive().optional(),
}).strict();

export const documentGenerationStatusResponseSchema = z.discriminatedUnion('status', [
  z.object({
    errorCode: documentGenerationErrorCodeSchema,
    generationId: z.uuid(),
    result: z.null(),
    status: z.enum(['dead', 'failed']),
  }).strict(),
  z.object({
    errorCode: z.null(),
    generationId: z.uuid(),
    result: z.null(),
    status: z.enum(['generating', 'pending']),
  }).strict(),
  z.object({
    errorCode: z.null(),
    generationId: z.uuid(),
    result: documentGenerationResultSchema,
    status: z.literal('succeeded'),
  }).strict(),
]).refine(
  (response) => response.status !== 'succeeded' || response.result.generationId === response.generationId,
  {
    error: 'Le résultat ne correspond pas à la génération demandée.',
    path: ['result', 'generationId'],
  },
);

export const generatedDocumentVersionsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
}).strict();

export const generatedDocumentVersionSchema = z.object({
  createdAt: z.string().datetime(),
  errorCode: documentGenerationErrorCodeSchema.nullable(),
  generationId: z.uuid(),
  kind: documentGenerationKindSchema,
  parentGenerationId: z.uuid().nullable(),
  revisionInstruction: z.string().trim().min(1).max(500).nullable(),
  revisionNumber: z.number().int().positive(),
  status: documentGenerationStatusSchema,
  title: z.string().trim().min(1).max(160),
}).strict();

export const generatedDocumentVersionsResponseSchema = z.object({
  items: z.array(generatedDocumentVersionSchema),
  latestSucceededGenerationId: z.uuid().nullable(),
  meta: z.object({
    page: z.number().int().positive(),
    pageCount: z.number().int().nonnegative(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
  }).strict(),
}).strict();

// --- contrat interne backend <-> backend-codegen ---
// Le contrat d'exécution contient le script Python, les inputs base64, le format
// cible et le nom de sortie. Il reste volontairement hors des DTO frontend :
// son implémentation de référence vit dans backend-codegen (Pydantic, PR 3).

export type DocumentContentPlanDTO = z.infer<typeof documentContentPlanSchema>;
export type DocumentContentPlanSectionDTO = z.infer<typeof documentContentPlanSectionSchema>;
export type DocumentBriefDTO = z.infer<typeof documentBriefSchema>;
export type DocumentCanonicalContentPlanDTO = z.infer<typeof documentCanonicalContentPlanSchema>;
export type DocumentConstraintDTO = z.infer<typeof documentConstraintSchema>;
export type DocumentFactualityModeDTO = z.infer<typeof documentFactualityModeSchema>;
export type DocumentGenerationAcceptedDTO = z.infer<typeof documentGenerationAcceptedSchema>;
export type DocumentGenerationSourceBriefDTO = z.infer<typeof documentGenerationSourceBriefSchema>;
export type DocumentGenerationSourceBriefRagCitationDTO = z.infer<typeof documentGenerationSourceBriefRagCitationSchema>;
export type DocumentGenerationSourceBriefUrlDTO = z.infer<typeof documentGenerationSourceBriefUrlSchema>;
export type DocumentGenerationErrorCodeDTO = z.infer<typeof documentGenerationErrorCodeSchema>;
export type DocumentGenerationKindDTO = z.infer<typeof documentGenerationKindSchema>;
export type DocumentGenerationOperationDTO = z.infer<typeof documentGenerationOperationSchema>;
export type DocumentPaletteDTO = z.infer<typeof documentPaletteSchema>;
export type DocumentPlannedSectionDTO = z.infer<typeof documentPlannedSectionSchema>;
export type DocumentProfileDTO = z.infer<typeof documentProfileSchema>;
export type DocumentRevisionPatchDTO = z.infer<typeof documentRevisionPatchSchema>;
export type DocumentSourceUsageDTO = z.infer<typeof documentSourceUsageSchema>;
export type DocumentTypographyDTO = z.infer<typeof documentTypographySchema>;
export type DocumentVisualComponentDTO = z.infer<typeof documentVisualComponentSchema>;
export type DocumentVisualPlanDTO = z.infer<typeof documentVisualPlanSchema>;
export type DocumentGenerationRequestDTO = z.infer<typeof documentGenerationRequestSchema>;
export type DocumentGenerationResultDTO = z.infer<typeof documentGenerationResultSchema>;
export type DocumentGenerationSpecDTO = z.infer<typeof documentGenerationSpecSchema>;
export type DocumentGenerationStatusDTO = z.infer<typeof documentGenerationStatusSchema>;
export type DocumentGenerationStatusResponseDTO = z.infer<typeof documentGenerationStatusResponseSchema>;
export type DocumentGenerationToolInputDTO = z.infer<typeof documentGenerationToolInputSchema>;
export type DocumentGenerationToolOutputDTO = z.infer<typeof documentGenerationToolOutputSchema>;
export type GeneratedDocumentVersionDTO = z.infer<typeof generatedDocumentVersionSchema>;
export type GeneratedDocumentVersionsQueryDTO = z.infer<typeof generatedDocumentVersionsQuerySchema>;
export type GeneratedDocumentVersionsResponseDTO = z.infer<typeof generatedDocumentVersionsResponseSchema>;
